import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CompanyService } from '../company.services';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { googleCalendarShareValidationSchema } from '../company.validations';
import type { InferType } from 'yup';
import { toast } from 'sonner';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GoogleCalendar } from '../company.types';
import { useNavigate, useParams } from 'react-router';
import { UserAccessService } from '@/features/user-access/user-access.service';

// Key localStorage utk inget status "connected" di card (persist lama).
const CARD_CONNECTED_KEY = 'gcal_card_connected';

const readCardConnected = (): boolean => {
  try {
    return localStorage.getItem(CARD_CONNECTED_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeCardConnected = (val: boolean): void => {
  try {
    if (val) localStorage.setItem(CARD_CONNECTED_KEY, 'true');
    else localStorage.removeItem(CARD_CONNECTED_KEY);
  } catch {
    /* ignore (storage gak tersedia) */
  }
};

/**
 * Handle status koneksi Google + flow "Connect to Google" pakai POPUP window
 * (bukan full-page redirect). Alurnya:
 *   1. GET /google/auth (JWT ke-attach) -> { url }
 *   2. window.open(url) -> user consent di Google
 *   3. Google redirect ke BE callback -> BE redirect ke FE /google/callback
 *   4. Halaman /google/callback postMessage ke window induk + nutup dirinya
 *   5. Hook ini denger message -> refetch status
 * Fallback: kalau popup di-close manual, kita poll `popup.closed`.
 */
const useGoogleCalendarConnection = () => {
  const queryClient = useQueryClient();
  const popupRef = useRef<Window | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  // Tampilan CARD: di-PERSIST di localStorage (tetap "connected" walau reload),
  // baru reset saat Disconnect. Terpisah dari status server (yg dipakai buat
  // fungsi binding di tabel), jadi binding tetap jalan walau card "Connect".
  const [sessionConnected, setSessionConnected] = useState(readCardConnected);

  const {
    data: status,
    isLoading: isStatusLoading,
    refetch,
  } = useQuery({
    queryKey: ['google-connection-status'],
    queryFn: CompanyService.getGoogleStatus,
    meta: { silentError: true },
  });

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const finishConnecting = useCallback(() => {
    setIsConnecting(false);
    clearPoll();
    void refetch();
    // Setelah connect/switch akun, akun aktif (mungkin) berubah -> segarkan
    // subcalendar list & summary biar ikut akun yang baru.
    queryClient.invalidateQueries({ queryKey: ['google-subcalendars'] });
    queryClient.invalidateQueries({ queryKey: ['google-binding-summary'] });
  }, [clearPoll, refetch, queryClient]);

  // Denger pesan dari halaman callback (/google/callback) di dalem popup.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'google-oauth') return;

      if (event.data.status === 'success') {
        toast.success('Google account connected successfully');
        setSessionConnected(true);
        writeCardConnected(true); // persist biar tetap connected walau reload
      } else {
        toast.error('Failed to connect Google account');
      }
      popupRef.current?.close();
      finishConnecting();
    };

    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
      clearPoll();
    };
  }, [finishConnecting, clearPoll]);

  const connect = useCallback(async () => {
    try {
      setIsConnecting(true);
      const { url } = await CompanyService.getGoogleAuthUrl();

      const w = 500;
      const h = 650;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;

      const popup = window.open(
        url,
        'google-oauth',
        `width=${w},height=${h},left=${left},top=${top}`
      );

      if (!popup) {
        setIsConnecting(false);
        toast.error('Popup blocked. Please allow popups and try again.');
        return;
      }

      popupRef.current = popup;

      // Fallback: kalau user nutup popup manual tanpa selesai.
      clearPoll();
      pollRef.current = setInterval(() => {
        if (popup.closed) finishConnecting();
      }, 600);
    } catch {
      setIsConnecting(false);
      toast.error('Failed to start Google authorization');
    }
  }, [clearPoll, finishConnecting]);

  // DISCONNECT (soft): cuma flip status; binding & token tetap.
  const disconnectMutation = useMutation({
    mutationFn: CompanyService.disconnectGoogle,
    onSuccess: res => {
      toast.success(res?.message ?? 'Google account disconnected');
      setSessionConnected(false);
      writeCardConnected(false); // hapus persist -> card balik ke "Connect"
      void refetch();
    },
    onError: (err: { message?: string }) =>
      toast.error(err?.message ?? 'Failed to disconnect Google account'),
  });

  return {
    // cardConnected: buat TAMPILAN card (default false tiap buka halaman).
    cardConnected: sessionConnected,
    // isConnected: status server (ada akun aktif) -> buat FUNGSI binding di tabel.
    isConnected: status?.connected ?? false,
    hasIntegration: status?.hasIntegration ?? false,
    connectedEmail: status?.email ?? null,
    isStatusLoading,
    isConnecting,
    connect,
    disconnect: disconnectMutation.mutate,
    isDisconnecting: disconnectMutation.isPending,
    refetchStatus: refetch,
  };
};

/**
 * Handle fitur BIND: lokasi <-> subcalendar dari akun Google yang connect.
 * - summary: jumlah subcalendar connected & yang udah ke-bind.
 * - bindings: map locationId -> binding (buat nempelin status di tabel lokasi).
 * - modal: state buka/tutup + lokasi yang lagi di-bind.
 * - subCalendars: list pilihan subcalendar (di-fetch pas modal kebuka).
 */
const useGoogleCalendarBind = (params?: { enabled?: boolean }) => {
  const enabled = params?.enabled ?? true;
  const queryClient = useQueryClient();

  const [selectedLocation, setSelectedLocation] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const isOpen = !!selectedLocation;

  const { data: summary } = useQuery({
    queryKey: ['google-binding-summary'],
    queryFn: CompanyService.getGoogleBindingSummary,
    enabled,
    meta: { silentError: true },
  });

  // Data tabel: SELALU di-fetch saat halaman dibuka, mau user udah connect atau
  // belum. getLocationBindings murni baca DB (tidak butuh token user).
  const { data: bindingsRes } = useQuery({
    queryKey: ['google-bindings'],
    queryFn: CompanyService.getGoogleBindings,
    meta: { silentError: true },
  });

  const bindingByLocation = useMemo(() => {
    const map = new Map<string, GoogleCalendar.ILocationBinding>();
    (bindingsRes?.data ?? []).forEach(b => map.set(b.locationId, b));
    return map;
  }, [bindingsRes]);

  const { data: subCalendarsRes, isLoading: subCalendarsLoading } = useQuery({
    queryKey: ['google-subcalendars'],
    queryFn: CompanyService.getGoogleSubCalendars,
    enabled: enabled && isOpen, // baru fetch pas modal kebuka
    meta: { silentError: true },
  });

  const subCalendarOptions = subCalendarsRes?.data ?? [];

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['google-bindings'] });
    queryClient.invalidateQueries({ queryKey: ['google-binding-summary'] });
  };

  const bindMutation = useMutation({
    mutationFn: CompanyService.bindLocationCalendar,
    onSuccess: res => {
      if (res?.ok) {
        toast.success(res.message ?? 'Location connected successfully');
        invalidateAll();
        setSelectedLocation(null);
      } else {
        toast.error(res?.message ?? 'Failed to connect location');
      }
    },
    onError: (err: { message?: string }) =>
      toast.error(err?.message ?? 'Failed to connect location'),
  });

  const unbindMutation = useMutation({
    mutationFn: CompanyService.unbindLocationCalendar,
    onSuccess: res => {
      toast.success(res?.message ?? 'Location disconnected');
      invalidateAll();
    },
    onError: (err: { message?: string }) =>
      toast.error(err?.message ?? 'Failed to disconnect location'),
  });

  const openBind = (location: { id: string; name: string }) =>
    setSelectedLocation(location);
  const closeBind = () => setSelectedLocation(null);

  const submitBind = (calendarId: string) => {
    if (!selectedLocation) return;
    bindMutation.mutate({ locationId: selectedLocation.id, calendarId });
  };

  return {
    summary: {
      subCalendarsConnected: summary?.subCalendarsConnected ?? 0,
      subCalendarsBound: summary?.subCalendarsBound ?? 0,
    },
    bindingByLocation,
    // modal
    isOpen,
    selectedLocation,
    openBind,
    closeBind,
    // subcalendar options
    subCalendarOptions,
    subCalendarsLoading,
    // actions
    submitBind,
    isBinding: bindMutation.isPending,
    unbind: unbindMutation.mutate,
    isUnbinding: unbindMutation.isPending,
  };
};

const useGoogleCalendarList = () => {
  const initialFilters: GoogleCalendar.IGoogleCalendarFilters = {
    search: '',
    page: 1,
    limit: 10,
    sort: '',
    order: '',
  };

  const [filters, setFilters] =
    useState<GoogleCalendar.IGoogleCalendarFilters>(initialFilters);

  const cleanFilters = useMemo(() => {
    const cleaned: Record<string, string | number> = {};

    (
      Object.entries(filters) as [
        keyof GoogleCalendar.IGoogleCalendarFilters,
        string | number | undefined,
      ][]
    ).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        cleaned[key] = value;
      }
    });

    if (filters.page !== undefined) cleaned.page = filters.page;
    if (filters.limit !== undefined) cleaned.limit = filters.limit;

    return cleaned;
  }, [filters]);

  const { data, isLoading } = useQuery({
    queryKey: ['google-calendar-list', cleanFilters],
    queryFn: CompanyService.getGoogleCalendarList,
  });

  const { data: branchesData } = useQuery({
    queryKey: ['branches', { page: 1, limit: 1000, status: 'active' }],
    queryFn: UserAccessService.getListLocation,
  });

  const subCalendarsAvailable = (branchesData?.data ?? []).filter(
    branch => !!branch.calendarId
  ).length;

  return { filters, setFilters, data, isLoading, subCalendarsAvailable };
};

const useGoogleCalendarShare = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams();
  const email = params.email ? decodeURIComponent(params.email) : '';
  const isEdit = !!email;

  const validationSchema = useMemo(
    () => googleCalendarShareValidationSchema(isEdit),
    [isEdit]
  );

  const form = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: '',
      subCalendars: [] as string[],
    },
  });

  /*
   * ----------------------------------------------------------------------
   * PREVIOUS SUB-CALENDAR LOGIC & FLOW (commented out)
   * ----------------------------------------------------------------------
   * Flow:
   *   1. Fetch all active branches/locations (limit 1000) via
   *      UserAccessService.getListLocation.
   *   2. Each branch may carry a `calendarId` (its linked Google
   *      sub-calendar). Branches without a `calendarId` are filtered out.
   *   3. The remaining branches are mapped into select options where
   *      value = branch.calendarId and label = branch.name. These options
   *      populate the multi-select "Sub Calendars" dropdown in form.tsx.
   *   4. `subCalendarsLoading` mirrored the branches query loading state so
   *      the dropdown could show a loading indicator.
   *
   * This dynamic, branch-derived list is temporarily replaced by a single
   * static "Primary" option below. Restore this block to bring back the
   * branch-driven sub-calendar selection.
   *
   * const { data: branchesData, isLoading: subCalendarsLoading } = useQuery({
   *   queryKey: ['branches', { page: 1, limit: 1000, status: 'active' }],
   *   queryFn: UserAccessService.getListLocation,
   * });
   *
   * const subCalendarOptions = useMemo(
   *   () =>
   *     (branchesData?.data ?? [])
   *       .filter(branch => !!branch.calendarId)
   *       .map(branch => ({
   *         value: branch.calendarId as string,
   *         label: branch.name ?? (branch.calendarId as string),
   *       })),
   *   [branchesData]
   * );
   * ----------------------------------------------------------------------
   */

  // Static sub-calendar options: only a single "Primary" option.
  const subCalendarsLoading = false;
  const subCalendarOptions = useMemo(
    () => [{ value: 'primary', label: 'Primary' }],
    []
  );

  const { data: shareData, isLoading: shareLoading } = useQuery({
    queryKey: ['google-calendar-share', email],
    queryFn: CompanyService.getGoogleCalendarByEmail,
    enabled: isEdit,
    select: res => res?.data,
    meta: { silentError: true },
  });

  useEffect(() => {
    if (shareData) {
      form.reset({
        email: shareData.email ?? '',
        subCalendars: (shareData.subCalendars ?? []).map(sub => sub.id),
      });
    }
  }, [shareData, form]);

  const mutation = useMutation({
    mutationFn: CompanyService.shareGoogleCalendar,
  });

  const onSubmit = (
    payload: InferType<ReturnType<typeof googleCalendarShareValidationSchema>>
  ) => {
    const isRemoval = isEdit && (payload.subCalendars?.length ?? 0) === 0;

    mutation.mutate(payload, {
      onSuccess: res => {
        if (isRemoval && email) {
          queryClient.removeQueries({
            queryKey: ['google-calendar-share', email],
          });
        } else if (email) {
          queryClient.invalidateQueries({
            queryKey: ['google-calendar-share', email],
          });
        }
        queryClient.invalidateQueries({ queryKey: ['google-calendar-list'] });
        toast.success(
          res.message ??
            (isRemoval
              ? 'Calendar share removed successfully'
              : isEdit
                ? 'Calendar updated successfully'
                : 'Calendar shared successfully')
        );
        navigate(-1);
      },
      onError: (err: { message?: string }) =>
        toast.error(
          err.message ??
            (isEdit ? 'Failed to update calendar' : 'Failed to share calendar')
        ),
    });
  };

  const handleBack = () => navigate(-1);

  return {
    form,
    onSubmit,
    handleBack,
    isEdit,
    isLoading: mutation.isPending || shareLoading,
    subCalendarOptions,
    subCalendarsLoading,
  };
};

export {
  useGoogleCalendarList,
  useGoogleCalendarShare,
  useGoogleCalendarConnection,
  useGoogleCalendarBind,
};
